import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged, signInWithPopup, signOut,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  updateProfile, sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc, setDoc, getDoc, onSnapshot, serverTimestamp,
} from "firebase/firestore";
import { auth, db, googleProvider } from "../lib/firebase";

const AuthContext = createContext(null);

// Free-tier lifetime allowance. Pro is unlimited. These are read in the app to
// gate photo/OCR adds and show usage indicators. Kept here so there's one
// source of truth the whole app imports.
export const FREE_LIMITS = { photos: 50, ocr: 25 };

// Fetch a single pending invite by id (used to show invite details on the
// landing page when someone arrives via ?invite=<id>).
export async function fetchInvite(inviteId) {
  if (!inviteId) return null;
  try {
    const s = await getDoc(doc(db, "pendingInvites", inviteId));
    if (!s.exists()) return null;
    return { id: s.id, ...s.data() };
  } catch (e) {
    console.error("fetchInvite failed", e);
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Live profile from users/{uid}: plan + usage counters. Kept in sync via an
  // onSnapshot so the usage indicators and gating update the moment the
  // trackUsageCounters trigger increments a count — no refresh needed.
  const [profile, setProfile] = useState(null);

  // Subscribe to the signed-in user's own doc for plan + usage. Re-subscribes
  // when the uid changes; cleans up on sign-out.
  useEffect(() => {
    if (!user?.uid) { setProfile(null); return; }
    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(ref, snap => {
      setProfile(snap.exists() ? snap.data() : null);
    }, err => {
      console.error("profile subscription error", err);
    });
    return unsub;
  }, [user?.uid]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userRef = doc(db, "users", firebaseUser.uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          await setDoc(userRef, {
            email: firebaseUser.email,
            name: firebaseUser.displayName || (firebaseUser.email || "").split("@")[0],
            photoURL: firebaseUser.photoURL || "",
            createdAt: serverTimestamp(),
            plan: "free",
          });
        } else {
          // Touch the doc so the redeemInvitesOnUserWrite cloud function fires
          // on every login. The function is idempotent — applying invites
          // when there are none is cheap, and this catches invites sent AFTER
          // the user already created their account.
          await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
        }
        // Note: pending-invite redemption is handled server-side by the
        // redeemInvitesOnUserWrite Cloud Function, which fires on every
        // users/{uid} write. It uses the Admin SDK to bypass security rules
        // — necessary because a brand-new user isn't yet a diary member,
        // so they can't add themselves under our rules.
        setUser(firebaseUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const signInWithGoogle = () => {
    googleProvider.setCustomParameters({ prompt: "select_account" });
    return signInWithPopup(auth, googleProvider);
  };

  const signUpWithEmail = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName && cred.user) {
      try { await updateProfile(cred.user, { displayName }); } catch (e) { console.error(e); }
    }
    return cred;
  };

  const signInWithEmail = (email, password) => signInWithEmailAndPassword(auth, email, password);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const logout = () => signOut(auth);

  // Derived plan/usage helpers for the whole app.
  const plan = profile?.plan || "free";
  const isPro = plan === "pro";
  const usage = {
    photos: profile?.usage?.photoCount || 0,
    ocr: profile?.usage?.ocrCount || 0,
  };
  // Whether the user can still add a photo / run an OCR scan. Pro is always
  // allowed; free is allowed until the lifetime cap is reached.
  const canAddPhotos = isPro || usage.photos < FREE_LIMITS.photos;
  const canAddOcr = isPro || usage.ocr < FREE_LIMITS.ocr;

  return (
    <AuthContext.Provider value={{
      user, loading,
      signInWithGoogle,
      signUpWithEmail, signInWithEmail, resetPassword,
      logout,
      // billing / usage
      plan, isPro, usage, limits: FREE_LIMITS,
      canAddPhotos, canAddOcr,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
